import {
    ActionIcon,
    CloseButton,
    Group,
    Menu,
    SegmentedControl,
    Select,
    Skeleton,
    Stack,
    Text,
    TextInput,
    Title,
    Tooltip,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { IconArrowDown, IconArrowUp, IconSearch } from "@tabler/icons-react";
import { queryOptions, useInfiniteQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { httpPostGraphql } from "shared/api/httpClient";
import { Content } from "shared/components/global/Content";
import { FloatingActionButton } from "shared/components/global/FloatingActionButton/FloatingActionButton";
import { LoadMoreTrigger } from "shared/components/global/LoadMoreTrigger";
import { IpawsTemplateCard } from "shared/components/ipaws/template/IpawsTemplateCard";
import { usePermissionCheck } from "shared/stores/oidc";
import { arr } from "shared/utils/array";
import { titlecase, to } from "shared/utils/fns";
import { createOffsetBasedGqlQueryOptions } from "shared/utils/query";
import { NoMessagesFigure } from "src/assets/NoMessagesFigure";
import { graphql } from "src/gql";
import {
    IpawsCertificateType,
    IpawsEventCode,
    IpawsTemplatesSearchParametersInput,
    IpawsTemplatesSortOptions,
    Permission,
    SortOrder,
} from "src/gql/graphql";

const Page = () => {
    const hasIpawsCreate = usePermissionCheck(Permission.IpawsAlertModify);

    const [search, setSearch] = useState("");
    const [debouncedSearch] = useDebouncedValue(search, 500);
    const [sortOption, setSortOption] = useState(IpawsTemplatesSortOptions.LastModified);
    const [sortDirection, setSortDirection] = useState(SortOrder.Desc);
    const [env, setEnv] = useState<IpawsCertificateType | "all">("all");
    const [selectedEventCode, setSelectedEventCode] = useState<string | null>(null);

    const filters = {
        query: debouncedSearch.length > 2 ? debouncedSearch : null,
        environment: env == "all" ? null : env,
        eventCode: selectedEventCode as IpawsEventCode | null,
        sort: { by: sortOption, direction: sortDirection },
    };

    const templates = useInfiniteQuery(queries.templates(filters));

    const certs = useSuspenseQuery(queries.certs);
    const eventCodes = arr(
        certs.data.ipawsCertificates
            .flatMap(to("channels"))
            .flatMap(to("eventCodes"))
            .map((evt) => ({ label: `${evt.id}: ${evt.description}`, value: evt.id })),
    )
        .dedup((a, b) => a.value == b.value)
        .take();

    return (
        <Content>
            <Content.Heading style={{ width: "100%" }}>
                <Stack w="100%">
                    <Group justify="space-between" w="100%">
                        <Title order={3} size="h1">
                            IPAWS Templates
                        </Title>
                        <SegmentedControl
                            data={[{ label: "All", value: "all" }].concat(
                                Object.values(IpawsCertificateType).map((env) => ({ label: titlecase(env), value: env })),
                            )}
                            onChange={(env) => setEnv(env as IpawsCertificateType | "all")}
                        />
                    </Group>
                    <Group w="100%" justify="space-between">
                        <Group>
                            <TextInput
                                placeholder="Search templates"
                                leftSection={<IconSearch size={16} />}
                                onChange={(e) => setSearch(e.target.value)}
                                value={search}
                                rightSection={
                                    search.length != 0 && (
                                        <CloseButton
                                            size="sm"
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => setSearch("")}
                                            aria-label="Clear value"
                                        />
                                    )
                                }
                                rightSectionPointerEvents={search.length == 0 ? "none" : "all"}
                            />
                            <Select
                                searchable
                                clearable
                                data={eventCodes}
                                onChange={(val) => setSelectedEventCode(val)}
                                placeholder="Event codes"
                                w={250}
                            />
                        </Group>
                        <Group gap="xs">
                            <Tooltip label={sortDirection == SortOrder.Desc ? "Descending" : "Ascending"}>
                                <ActionIcon
                                    variant="default"
                                    size="input-sm"
                                    onClick={() => setSortDirection(sortDirection != SortOrder.Desc ? SortOrder.Desc : SortOrder.Asc)}
                                >
                                    {sortDirection == SortOrder.Desc ? <IconArrowDown /> : <IconArrowUp />}
                                </ActionIcon>
                            </Tooltip>
                            <Select
                                allowDeselect={false}
                                defaultValue={IpawsTemplatesSortOptions.LastModified}
                                data={[
                                    { label: "Last Modified", value: IpawsTemplatesSortOptions.LastModified },
                                    { label: "Created", value: IpawsTemplatesSortOptions.Created },
                                    { label: "Event Code (Alphabetical)", value: IpawsTemplatesSortOptions.EventCode },
                                ]}
                                onChange={(_, { value }) => setSortOption(value as IpawsTemplatesSortOptions)}
                            />
                        </Group>
                    </Group>
                </Stack>
            </Content.Heading>
            {templates.isPlaceholderData ? (
                arr.make(4, (i) => <Skeleton key={`skeleton-${i}`} h={150} />).take()
            ) : templates?.data?.pages.flat().length == 0 ? (
                <NoMessagesFigure>
                    <Text ta={"center"}>
                        Looks like you don't have any IPAWS templates yet, you can&nbsp;
                        <Link to="/ipaws/templates/new">
                            <Text span c="brand">
                                click here
                            </Text>
                        </Link>
                        &nbsp;to get started!
                    </Text>
                </NoMessagesFigure>
            ) : (
                templates?.data?.pages.flat().map((t) => <IpawsTemplateCard key={t.id} ipawsTemplate={t} />)
            )}

            {hasIpawsCreate && (
                <FloatingActionButton>
                    <Menu.Item component={Link} to="/ipaws/templates/new">
                        New IPAWS Alert Template
                    </Menu.Item>
                </FloatingActionButton>
            )}

            <LoadMoreTrigger query={queries.templates(filters)} />
        </Content>
    );
};

const queries = {
    templates: (search?: IpawsTemplatesSearchParametersInput) =>
        createOffsetBasedGqlQueryOptions({
            queryKey: ["ipaws", "templates", "list", "search", JSON.stringify(search)],
            document: graphql(`
                query IpawsTemplatesList($limit: Int!, $offset: Int!, $search: IpawsTemplatesSearchParametersInput) {
                    ipawsTemplates(limit: $limit, offset: $offset, search: $search) {
                        id
                        alert {
                            ...IpawsAlertThumbnail
                        }
                    }
                }
            `),
            select: (data) => data.ipawsTemplates,
            variables: { search },
        }),
    certs: queryOptions({
        queryKey: ["ipaws", "eventCodes", "list"],
        queryFn: () =>
            httpPostGraphql(
                graphql(`
                    query IpawsTemplatesListCertificates {
                        ipawsCertificates {
                            channels {
                                eventCodes {
                                    id
                                    description
                                }
                            }
                        }
                    }
                `),
            ),
    }),
};

/** @public */
export const Route = createFileRoute("/_auth/ipaws/templates/")({
    component: Page,
});
